import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { of } from 'rxjs';

import { CommonModule } from '@angular/common';
import { Messages } from '../../../../messages/Messages';
import { SelectedSuggestion, Suggestion } from '../../../../model/suggestions';
import { OrtFacettenService } from '../../../../services/facetten/instance/ort-facetten.service';
import { TooltipDirective } from '../../../../shared/directives/tooltip/tooltip.directive';
import { LoadSuggestions, TypeaheadComponent } from '../../../typeahead/typeahead.component';
import { GeolocationService } from '../../../../services/suchseite/geolocation.service';

@Component({
  selector: 'now-wbsuche-lib-ort-suche-suchzeile',
  templateUrl: './ort-suche-suchzeile.component.html',
  styleUrls: ['ort-suche-suchzeile.component.scss'],
  imports: [CommonModule, TooltipDirective, TypeaheadComponent]
})
export class OrtSucheSuchzeileComponent implements OnChanges {
  geolocationService = inject(GeolocationService);
  protected ortFacettenService = inject(OrtFacettenService);

  @Input()
  postleitzahl?: string;

  public messages = Messages;

  ladeGeolocation: boolean = false;
  geolocationPositionError: GeolocationPositionError | null = null;
  //  Muster für ein Suchwort beispielsweise für Orte (erlaubter Zeichensatz ist gleich)
  private readonly ORT_BERUF_SUCHWORT_PATTERN =
    "[\\sa-zA-ZäöüàâæçèéêëîïôœùûÿÀÂÆÇÈÉÊËÎÏÔŒÙÛŸÄÖÜß/().,\\-']+";
  //  Muster für die Suchwort Eingabe im Ortssuchfeld (Erlaubt sind Ortsnamen oder eine PLZ)
  private readonly VALIDATE_REGEX_ORT_EINGABE =
    '^(\\s*|\\d{1,5}|' + this.ORT_BERUF_SUCHWORT_PATTERN + ')$';
  //  Muster ausschließlich für die Validierung einer PLZ
  private readonly VALIDATE_REGEX_PLZ = '^(\\d{1,5})$';
  private readonly AUTOSUGGEST_PLZ_LEN = 2;
  private readonly AUTOSUGGEST_MINLEN = 2;

  public ortLabel$ = this.ortFacettenService.ortLabel$;

  // Async, weil sich die URL-setze-Funktionen nicht überschlagen und nur nacheinander ausgeführt werden dürfen
  async ngOnChanges(changes: SimpleChanges) {
    if (changes.postleitzahl?.currentValue) {
      const plz = changes.postleitzahl?.currentValue;

      if (plz) {
        await this.ortFacettenService.setzePlz(plz);
      }
    }
  }

  public loadSuggestions: LoadSuggestions = (searchText: string) => {
    const inputVal = searchText.trim();

    const validationError = this.getValidationError(inputVal);
    if (validationError) {
      return of([validationError]);
    }
    return this.ortFacettenService.loadSuggestions(searchText);
  };

  public onSuggestionSelected(selectedSuggestion: SelectedSuggestion | null) {
    this.ortFacettenService.onSuggestionSelected(selectedSuggestion);
    this.resetGeoLocationLoadingAndError();
  }

  /**
   * setze Lade-Status und Fehlernachricht zurück.
   */
  private resetGeoLocationLoadingAndError() {
    // Nach erfolgreicher Nutzereingabe setze Lade-Status und Fehlernachricht zurück.
    this.ladeGeolocation = false;
    this.geolocationPositionError = null;
  }

  /**
   * Fragt den Nutzer über den Browser nach seinem Standort.
   * Währenddessen wird der Status auf Laden gesetzt.
   * Im Falle eines Fehlers wird dieser hinterlegt.
   */
  public async locationFromBrowser(e: Event) {
    e.preventDefault();

    this.ladeGeolocation = true;
    this.geolocationPositionError = null;

    try {
      const ort = await this.geolocationService.loadCurrentPosition();
      this.ortFacettenService.setOrt(ort);
      this.resetGeoLocationLoadingAndError();
    } catch (e) {
      this.geolocationPositionError = e as GeolocationPositionError;
    } finally {
      this.ladeGeolocation = false;
    }
  }

  /**
   * Wahr gdw. String leer oder null ist.
   */
  isEmpty(str: string | null) {
    return (str?.length ?? 0) === 0;
  }

  private getValidationError(searchText: string): Suggestion | null {
    return (
      this.getValidationForWrongOrtInput(searchText) ||
      this.getValidationForWrongPLZInput(searchText) ||
      this.getValidationMinLengthInput(searchText)
    );
  }

  private getValidationForWrongOrtInput(searchText: string): Suggestion | null {
    const valid = new RegExp(this.VALIDATE_REGEX_ORT_EINGABE, 'i').test(searchText);
    if (!valid) {
      return Suggestion.error(Messages.ORT_EINGABEPARAMETER_FEHLERHAFT);
    }
    return null;
  }

  private getValidationForWrongPLZInput(searchText: string): Suggestion | null {
    const numeric = new RegExp(this.VALIDATE_REGEX_PLZ).test(searchText);
    if (numeric && searchText.length < this.AUTOSUGGEST_PLZ_LEN) {
      return Suggestion.error(Messages.ORT_EINGABEPARAMETER_PLZ);
    }
    return null;
  }

  private getValidationMinLengthInput(searchText: string): Suggestion | null {
    const numeric = new RegExp(this.VALIDATE_REGEX_PLZ).test(searchText);
    if (!numeric && searchText.length < this.AUTOSUGGEST_MINLEN) {
      return Suggestion.info(Messages.ORT_EINGABE_MIN_LENGTH(this.AUTOSUGGEST_MINLEN));
    }
    return null;
  }
}
